import { useMemo } from 'react';
import {
  EntityWithDocumentFullResponse,
  ShareholderDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import {
  ListShareholder,
  RenderIndividualPersonsDetails,
  renderEntityContactDetails,
  renderEntityDetailsSection,
  renderEntityDocuments,
  renderEntityTaxResidence,
} from '@/modules/entity/utils/entity-info/entity-info.tsx';

export const RenderSmsfEntityDetails = ({
  smsf_entity_details,
  document_files,
  ...entity_details
}: Partial<EntityWithDocumentFullResponse>) => {
  const {
    country,
    abn,
    beneficiaries,
    trustee,
    settlors,
    registered_office,
    is_us_person,
    non_us_person_certification,
    tax_residence,
    account_holders_gin,
    sponsoring_entity_name,
    is_managed_by_another_institution,
    crs_status,
    company_name,
  } = smsf_entity_details || {};

  const { bank_name, bank_account_bsb, bank_account_number, bank_account_name } =
    entity_details || {};

  const subShareholders = useMemo<ListShareholder[]>(() => {
    const trustee2 = (smsf_entity_details?.trustee || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'trustee',
        navName: `Trustee`,
      }),
    );
    const beneficiaries2 = (smsf_entity_details?.beneficiaries || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'beneficiaries',
        navName: `Beneficiary`,
      }),
    );

    const settlors2 = (smsf_entity_details?.settlors || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'settlors',
        navName: `Settlor`,
      }),
    );
    return [...trustee2, ...beneficiaries2, ...settlors2];
  }, [
    smsf_entity_details?.beneficiaries,
    smsf_entity_details?.settlors,
    smsf_entity_details?.trustee,
  ]);

  return (
    <>
      {renderEntityContactDetails(entity_details)}
      {renderEntityDetailsSection({
        name: 'Self Managed Superannuation Fund details',
        items: [
          { label: 'Name', value: company_name },
          { label: 'Country', value: country },
          { label: 'ABN', value: abn },
          { label: 'Registered office', value: registered_office },
        ],
      })}
      {RenderIndividualPersonsDetails({
        persons: trustee,
        role: 'Trustee',
        listStakeholders: subShareholders,
      })}
      {RenderIndividualPersonsDetails({
        persons: beneficiaries,
        role: 'Beneficiary',
        listStakeholders: subShareholders,
      })}
      {RenderIndividualPersonsDetails({
        persons: settlors,
        role: 'Settlor',
        listStakeholders: subShareholders,
      })}

      {renderEntityTaxResidence(tax_residence)}
      {renderEntityDetailsSection({
        items: [
          { label: 'GIIN', value: account_holders_gin },
          { label: 'Sponsoring entity name', value: sponsoring_entity_name },
          {
            label:
              'Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or resident alien of the U.S. even if residing outside the U.S.',
            value: is_us_person,
          },
          { label: 'Non U.S Person Certification', value: non_us_person_certification },

          {
            label: 'Is the entity an Investment Entity managed by another Financial Institution?',
            value: is_managed_by_another_institution,
          },
          { label: 'CRS status', value: crs_status },
        ],
      })}
      {renderEntityDetailsSection({
        name: 'Payment instructions',
        items: [
          { label: 'Bank name', value: bank_name },
          { label: 'BSB', value: bank_account_bsb },
          { label: 'Account number', value: bank_account_number },
          { label: 'Account name', value: bank_account_name },
        ],
      })}
      {renderEntityDocuments(document_files || [])}
    </>
  );
};
